import { Button, makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import React, { useState } from 'react'
import WorkOrderNavMenu from './WorkOrderNavMenu'

export interface OptionsConsumerToggleProps {
  value: 'crew' | 'customer'
  onChange: (value: 'crew' | 'customer') => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      button: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(13),
        minHeight: 30,
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.borderRadius.md,
        '&&': {
          color: neutral.P60
        },
        '& .MuiButton-startIcon': {
          marginRight: theme.spacing(0.75)
        },
        '&:hover': {
          backgroundColor: neutral.P97
        },
        '@media print': {
          display: 'none'
        }
      }
    }
  },
  { name: 'OptionsConsumerToggle' }
)

const CONSUMER_OPTIONS: Array<{ value: 'crew' | 'customer'; label: string }> = [
  { value: 'customer', label: 'Customer/Price' },
  { value: 'crew', label: 'Crew/Hours' }
]

/**
 * The Work Order Options card's display switcher — matches the legacy `WorkOrderView`'s
 * `AreaSubstrateView` control (a Visibility icon + label opening a menu of Customer/Price vs
 * Crew/Hours), ported onto the shared estimateNext Button/Menu pattern (see the nav bar's hours
 * dropdown in `WorkOrderNavBarNext`). Switches whether the Options card shows customer prices or
 * crew hours (`quote.options.workOrderOptionsConsumer`). The list is the work order's shared menu
 * (`WorkOrderNavMenu`, ENG-2080).
 */
export default function OptionsConsumerToggle({ value, onChange }: OptionsConsumerToggleProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const selected = CONSUMER_OPTIONS.find((option) => option.value === value) ?? CONSUMER_OPTIONS[0]

  return (
    <>
      <Button
        variant="text"
        size="small"
        className={classes.button}
        startIcon={<VisibilityIcon />}
        endIcon={anchor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={(ev: React.MouseEvent<HTMLElement>) => setAnchor(ev.currentTarget)}
      >
        {__(selected.label)}
      </Button>
      <WorkOrderNavMenu
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        align="right"
        minWidth={180}
        items={CONSUMER_OPTIONS.map((option) => ({
          key: option.value,
          label: __(option.label),
          selected: option.value === value,
          onSelect: () => onChange(option.value)
        }))}
      />
    </>
  )
}
